import React, { useState, useEffect } from "react";
import { Form, Spin, notification, Checkbox } from "antd";
import { connect } from "react-redux";
import { getIntegrationSettings, saveIntegrationSettings, testIntegrationConnection } from "../../../services/integrationService";
import EditableFormInput from "../../../common/components/EditableText/EditableFormInput";
import CardBlock from "src/common/components/Card";

const Integrations = ({ form }) => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // State for form values
  const [etherfaxEnabled, setEtherfaxEnabled] = useState(false);
  const [etherfaxUsername, setEtherfaxUsername] = useState("");
  const [etherfaxPassword, setEtherfaxPassword] = useState("");
  const [etherfaxAccountId, setEtherfaxAccountId] = useState("");
  const [etherfaxApiKey, setEtherfaxApiKey] = useState("");
  const [etherfaxCallbackUrl, setEtherfaxCallbackUrl] = useState("");
  const [etherfaxFaxFromNumber, setEtherfaxFaxFromNumber] = useState("");

  useEffect(() => {
    fetchEtherfaxSettings();
  }, []);

  const fetchEtherfaxSettings = async () => {
    try {
      setInitialLoading(true);
      const response = await getIntegrationSettings("etherfax");
      if (response.data && response.data.data) {
        const data = response.data.data;
        setEtherfaxEnabled(data.is_active || false);
        // Extract from credentials object
        const credentials = data.credentials || {};
        setEtherfaxUsername(credentials.username || "");
        setEtherfaxPassword(credentials.password || "");
        setEtherfaxAccountId(credentials.account_id || "");
        setEtherfaxApiKey(credentials.api_key || "");
        setEtherfaxCallbackUrl(credentials.callback_url || "");
        setEtherfaxFaxFromNumber(credentials.fax_from_number || "");
      }
    } catch (error) {
      console.error("Error fetching Etherfax settings:", error);
      notification.error({
        message: "Error",
        description: "Failed to load Etherfax settings.",
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const onFinish = (e) => {
    e.preventDefault();

    const payload = {
      credentials: {
        username: etherfaxUsername,
        password: etherfaxPassword,
        account_id: etherfaxAccountId,
        api_key: etherfaxApiKey,
        callback_url: etherfaxCallbackUrl,
        fax_from_number: etherfaxFaxFromNumber,
      },
      settings: {},
      is_active: etherfaxEnabled,
    };

    setLoading(true);

    saveIntegrationSettings("etherfax", payload)
      .then(() => {
        notification.success({
          message: "Success",
          description: "Etherfax integration settings saved successfully.",
        });
      })
      .catch((error) => {
        console.error("Error saving Etherfax settings:", error);
        notification.error({
          message: "Error",
          description: "Failed to save Etherfax settings. Please try again.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTestConnection = () => {
    const payload = {
      credentials: {
        username: etherfaxUsername,
        password: etherfaxPassword,
        account_id: etherfaxAccountId,
        api_key: etherfaxApiKey,
        callback_url: etherfaxCallbackUrl,
        fax_from_number: etherfaxFaxFromNumber,
      },
      settings: {},
      is_active: etherfaxEnabled,
    };

    setTestLoading(true);

    // Save the current form values first
    saveIntegrationSettings("etherfax", payload)
      .then(() => {
        // Then test the connection
        return testIntegrationConnection("etherfax");
      })
      .then(() => {
        notification.success({
          message: "Connection Test Successful",
          description: "Successfully connected to Etherfax API.",
        });
      })
      .catch((error) => {
        console.error("Error testing Etherfax connection:", error);
        notification.error({
          message: "Connection Test Failed",
          description: "Failed to connect to Etherfax API. Please check your settings.",
        });
      })
      .finally(() => {
        setTestLoading(false);
      });
  };

  const handleFormKeyDown = (event) => {
    if (event.key === "Enter") {
      onFinish(event);
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="clinicWorkflow__main-section" style={{ padding: "0 0.5rem 0 1rem", height: "calc(100vh)" }}>
      <div className="fullContainer has-no-sidebar">
        <div
          className="mainContainer"
          style={{
            marginLeft: "-10px",
            paddingTop: "0",
            paddingBottom: "0",
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            alignItems: "stretch",
            width: "67%",
          }}
        >
          <CardBlock style={{ maxHeight: "100%", height: "100%" }} isOverFlow={true} title="Etherfax Integration Settings" varient="half" other="">
            <Form layout="vertical" onSubmit={onFinish} style={{ width: "100%" }}>
              <Checkbox
                onChange={(value) => setEtherfaxEnabled(value.target.checked)}
                checked={etherfaxEnabled}
                style={{ width: "100%", margin: "5px 0 2rem 0" }}
              >
                Enable Etherfax Integration
              </Checkbox>
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Etherfax Username"
                id="etherfax_username"
                value={etherfaxUsername}
                handleChange={(value) => setEtherfaxUsername(value)}
                required={true}
                fullWidth={true}
                helperText="Etherfax username is required!"
                onKeyDown={handleFormKeyDown}
              />
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Etherfax Password"
                id="etherfax_password"
                value={etherfaxPassword}
                handleChange={(value) => setEtherfaxPassword(value)}
                required={true}
                fullWidth={true}
                type="password"
                helperText="Etherfax password is required!"
                onKeyDown={handleFormKeyDown}
              />
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Etherfax Account ID"
                id="etherfax_account_id"
                value={etherfaxAccountId}
                handleChange={(value) => setEtherfaxAccountId(value)}
                required={true}
                fullWidth={true}
                helperText="Etherfax account ID is required!"
                onKeyDown={handleFormKeyDown}
              />
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Etherfax API Key"
                id="etherfax_api_key"
                value={etherfaxApiKey}
                handleChange={(value) => setEtherfaxApiKey(value)}
                required={true}
                fullWidth={true}
                helperText="Etherfax API Key is required!"
                onKeyDown={handleFormKeyDown}
              />
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Fax From Number"
                id="etherfax_fax_from_number"
                value={etherfaxFaxFromNumber}
                handleChange={(value) => setEtherfaxFaxFromNumber(value)}
                required={true}
                fullWidth={true}
                helperText="Fax From Number is required!"
                onKeyDown={handleFormKeyDown}
              />
              <div style={{ marginTop: "0.5rem" }} />
              <EditableFormInput
                label="Callback URL"
                id="etherfax_callback_url"
                value={etherfaxCallbackUrl}
                handleChange={(value) => setEtherfaxCallbackUrl(value)}
                required={false}
                fullWidth={true}
                helperText="Callback URL (optional)"
                onKeyDown={handleFormKeyDown}
              />

              <div className="actionContent" style={{ flexWrap: "wrap", marginTop: "1.5rem", textAlign: "right" }}>
                <button
                  onClick={onFinish}
                  onKeyDown={handleFormKeyDown}
                  className="common-btn blue-btn"
                  style={{ marginRight: "0.5rem" }}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Settings"}
                </button>
                <button onClick={handleTestConnection} onKeyDown={handleFormKeyDown} className="common-btn outlined-btn" disabled={testLoading}>
                  {testLoading ? "Testing..." : "Test Connection"}
                </button>
              </div>
            </Form>
          </CardBlock>
        </div>
      </div>
    </div>
  );
};

export default connect()(Integrations);
